import { CommandLineIcon, TrashIcon } from "@heroicons/react/24/outline";
import type {
  ChannelSessionDescriptor,
  ClaudeChannelConversation,
} from "common";

export const EMPTY_SESSIONS_COPY =
  "Start Claude Code in a terminal with the Foundry Connect channel to see it here.";
export const NOT_A_MIRROR_COPY =
  "This shows what you send from here and the session's approval requests. It is not a copy of your terminal.";

const formatTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "unknown time";

interface SessionListProps {
  sessions: ChannelSessionDescriptor[];
  conversations: ClaudeChannelConversation[];
  onNewConversation: (sessionId: string) => void;
  onSelectConversation: (conversationId: number) => void;
  onDeleteConversation: (conversationId: number) => void;
}

const ConversationRow = ({
  conversation,
  onSelect,
  onDelete,
}: {
  conversation: ClaudeChannelConversation;
  onSelect: () => void;
  onDelete: () => void;
}) => (
  <li className="flex items-center justify-between gap-2 rounded-md px-2 py-1 hover:bg-gray-100">
    <button
      type="button"
      onClick={onSelect}
      className="min-w-0 flex-1 truncate text-left text-sm text-gray-800">
      {conversation.title || "Untitled conversation"}
    </button>
    <button
      type="button"
      aria-label={`Delete ${conversation.title || "conversation"}`}
      onClick={onDelete}
      className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600">
      <TrashIcon className="h-4 w-4" />
    </button>
  </li>
);

export const SessionList = ({
  sessions,
  conversations,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
}: SessionListProps) => {
  const live = new Set(sessions.map(s => s.sessionId));
  const ended = conversations.filter(c => !live.has(c.sessionId));

  return (
    <div className="space-y-4">
      {sessions.length === 0 ? (
        <p
          data-testid="claude-sessions-empty"
          className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
          {EMPTY_SESSIONS_COPY}
        </p>
      ) : (
        <ul className="space-y-3" aria-label="Claude Code sessions">
          {sessions.map(s => (
            <li
              key={s.sessionId}
              data-testid="claude-session"
              className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex items-start gap-2">
                <CommandLineIcon className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {s.machine}
                  </p>
                  <p
                    className="truncate font-mono text-xs text-gray-600"
                    title={s.folder}>
                    {s.folder}
                  </p>
                  <p className="text-xs text-gray-500">
                    Started {formatTime(s.startedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNewConversation(s.sessionId)}
                  className="shrink-0 rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
                  New conversation
                </button>
              </div>
              <ul className="mt-2 space-y-1">
                {conversations
                  .filter(c => c.sessionId === s.sessionId)
                  .map(c => (
                    <ConversationRow
                      key={c.id}
                      conversation={c}
                      onSelect={() => onSelectConversation(c.id)}
                      onDelete={() => onDeleteConversation(c.id)}
                    />
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-500">{NOT_A_MIRROR_COPY}</p>
      {ended.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Ended sessions
          </p>
          <ul
            className="space-y-1"
            aria-label="Conversations of ended sessions">
            {ended.map(c => (
              <ConversationRow
                key={c.id}
                conversation={c}
                onSelect={() => onSelectConversation(c.id)}
                onDelete={() => onDeleteConversation(c.id)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
